import { useState } from 'react'
import { motion } from 'framer-motion'
import { FiToggleLeft, FiToggleRight, FiTrash2, FiInfo } from 'react-icons/fi'
import { useSettings } from '../contexts/SettingsContext'
import { useChat } from '../contexts/ChatContext'

const Settings = () => {
  const { settings, updateSettings } = useSettings()
  const { clearChat, detectedLanguage, userStyle } = useChat()
  const [showClearConfirm, setShowClearConfirm] = useState(false)
  const [showStyleInfo, setShowStyleInfo] = useState(false)
  
  const handleToggle = (setting) => {
    updateSettings({ [setting]: !settings[setting] })
  }
  
  const handleSelect = (setting, value) => {
    updateSettings({ [setting]: value })
  }
  
  const handleClearChat = () => {
    clearChat()
    setShowClearConfirm(false)
  }
  
  const avatarStyles = [
    { id: 'holographic', label: 'Holographic' },
    { id: 'cartoon', label: 'Cartoon' }
  ]
  
  const languages = [
    { id: 'auto', label: 'Auto-detect' },
    { id: 'english', label: 'English' },
    { id: 'tagalog', label: 'Tagalog' },
    { id: 'spanish', label: 'Spanish' },
    { id: 'french', label: 'French' }
  ]
  
  const formatStyleValue = (value) => {
    return value.charAt(0).toUpperCase() + value.slice(1)
  }
  
  return (
    <motion.div 
      className="min-h-screen pt-20 pb-6 px-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="max-w-2xl mx-auto">
        <h1 className="text-2xl md:text-3xl font-display text-white mb-6">Settings</h1>
        
        <div className="glass-panel rounded-xl overflow-hidden">
          <div className="settings-option">
            <div>
              <h3 className="text-white font-medium">Voice Output</h3>
              <p className="text-gray-400 text-sm">Enable text-to-speech for Lyo's responses</p>
            </div>
            <button 
              onClick={() => handleToggle('voiceEnabled')}
              className="text-2xl text-gray-400"
            >
              {settings.voiceEnabled ? (
                <FiToggleRight className="text-neon-cyan" />
              ) : (
                <FiToggleLeft />
              )}
            </button>
          </div>
          
          <div className="settings-option">
            <div>
              <h3 className="text-white font-medium">Avatar Style</h3>
              <p className="text-gray-400 text-sm">Choose how Lyo appears</p>
            </div>
            <div className="flex gap-2">
              {avatarStyles.map(style => (
                <button
                  key={style.id}
                  onClick={() => handleSelect('avatarStyle', style.id)}
                  className={`px-3 py-1.5 text-xs rounded-full transition-all duration-300 ${
                    settings.avatarStyle === style.id
                      ? 'bg-neon-cyan text-midnight'
                      : 'bg-glass-light text-gray-300 hover:bg-glass-medium'
                  }`}
                >
                  {style.label}
                </button>
              ))}
            </div>
          </div>
          
          <div className="settings-option">
            <div>
              <h3 className="text-white font-medium">Language Preference</h3>
              <p className="text-gray-400 text-sm">
                Set language preference (Currently detected: {formatStyleValue(detectedLanguage)})
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              {languages.map(lang => (
                <button
                  key={lang.id}
                  onClick={() => handleSelect('language', lang.id)}
                  className={`px-3 py-1.5 text-xs rounded-full transition-all duration-300 ${
                    settings.language === lang.id
                      ? 'bg-neon-cyan text-midnight'
                      : 'bg-glass-light text-gray-300 hover:bg-glass-medium'
                  }`}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          </div>

          <div className="settings-option">
            <div>
              <h3 className="text-white font-medium flex items-center">
                Communication Style Analysis
                <button
                  onClick={() => setShowStyleInfo(!showStyleInfo)}
                  className="ml-2 text-gray-400 hover:text-white"
                >
                  <FiInfo size={16} />
                </button>
              </h3>
              <p className="text-gray-400 text-sm">Lyo adapts to your communication style</p>
              {showStyleInfo && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-3 p-3 bg-glass-dark rounded-lg text-xs"
                >
                  <div className="grid grid-cols-2 gap-2 text-gray-300">
                    <div>
                      <span className="text-neon-cyan">Humor:</span> {formatStyleValue(userStyle.humor)}
                    </div>
                    <div>
                      <span className="text-neon-cyan">Formality:</span> {formatStyleValue(userStyle.formality)}
                    </div>
                    <div>
                      <span className="text-neon-cyan">Expression:</span> {formatStyleValue(userStyle.emotionalExpression)}
                    </div>
                    <div>
                      <span className="text-neon-cyan">Style:</span> {formatStyleValue(userStyle.communicationStyle)}
                    </div>
                  </div>
                  <p className="text-gray-400 mt-2">
                    Lyo learns from your messages and adapts responses to match your communication style.
                  </p>
                </motion.div>
              )}
            </div>
          </div>
          
          <div className="settings-option">
            <div>
              <h3 className="text-white font-medium">Notifications</h3>
              <p className="text-gray-400 text-sm">Receive insight and reflection reminders</p>
            </div>
            <button 
              onClick={() => handleToggle('notifications')}
              className="text-2xl text-gray-400"
            >
              {settings.notifications ? (
                <FiToggleRight className="text-neon-cyan" />
              ) : (
                <FiToggleLeft />
              )}
            </button>
          </div>

          <div className="settings-option border-b-0">
            <div>
              <h3 className="text-white font-medium">Clear Chat History</h3>
              <p className="text-gray-400 text-sm">Delete all chat messages and reset style analysis</p>
            </div>
            {showClearConfirm ? (
              <div className="flex gap-2">
                <button
                  onClick={handleClearChat}
                  className="px-3 py-1.5 text-xs rounded-full bg-error-DEFAULT text-white hover:bg-error-dark transition-all duration-300"
                >
                  Confirm
                </button>
                <button
                  onClick={() => setShowClearConfirm(false)}
                  className="px-3 py-1.5 text-xs rounded-full bg-glass-light text-gray-300 hover:bg-glass-medium transition-all duration-300"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button 
                onClick={() => setShowClearConfirm(true)}
                className="text-2xl text-gray-400 hover:text-error-DEFAULT transition-colors duration-300"
              >
                <FiTrash2 className="w-6 h-6" />
              </button>
            )}
          </div>
        </div>
        
        <div className="mt-8 text-center">
          <p className="text-gray-500 text-sm">Lyo v0.1.0 - Emotionally Intelligent AI</p>
          <p className="text-gray-600 text-xs mt-1">
            Adapts to your language and communication style automatically
          </p>
        </div>
      </div>
    </motion.div>
  )
}

export default Settings